import React, { useState, useEffect } from 'react';
import OrderPage from './components/OrderPage';
import CartPage from './components/CartPage';
import OrderHistory from './components/OrderHistory';
import MasterDashboard from './components/MasterAdmin/MasterDashboard';
import { SellerDashboard } from './components/SellerAdmin/SellerDashboard';
import LandingPage from './components/LandingPage';
import NotFoundPage from './components/NotFoundPage';
import {
  Product,
  Order,
  Store,
  UserProfile,
  OrderSheet,
} from './types';
import {
  getAllOrders,
  updateOrderStatus,
} from './api/orders';
import { getStores, createStore, updateStore } from './api/stores';
import { getProducts, createProduct, getAdminProducts } from './api/products';
import { updateProductWithStockConfirmation } from './utils/productStockReduction';
import { getActiveOrderSheet, getOrderSheets } from './api/order-sheets';
import { getCustomerAliases, updateCustomerAlias } from './api/customers';


import { getKakaoToken, kakaoLogin, getProfile, impersonate } from './api/auth';

import LoginPage from './components/LoginPage';
import client from './api/client';

const App: React.FC = () => {
  type ViewState = 'LANDING' | 'FORM' | 'CART' | 'HISTORY' | 'SELLER' | 'LOGIN' | 'SELLER_LOGIN' | 'MASTER' | 'MASTER_LOGIN' | 'NOT_FOUND' | 'LOADING';

  const getInitialView = (): ViewState => {
    const path = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    if (params.get('code')) {
      return 'LOADING';
    }
    if (path === '/back-master') {
      return localStorage.getItem('master_token') ? 'MASTER' : 'MASTER_LOGIN';
    }
    if (path === '/backstage' || path.startsWith('/backstage/')) {
      return localStorage.getItem('seller_token') ? 'SELLER' : 'SELLER_LOGIN';
    }
    if (path === '/' || path === '' || path === '/index.html') {
      return 'LANDING';
    }
    return 'LOADING';
  };

  const [view, setView] = useState<ViewState>(getInitialView());

  const [products, setProducts] = useState<Product[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [customerAliases, setCustomerAliases] = useState<Record<string, string>>({});
  const [currentStoreId, setCurrentStoreId] = useState<string>('');

  // Routing Logic
  const handleRoute = async () => {
    const path = window.location.pathname;
    const segments = path.split('/').filter(Boolean);

    // 1. Admin/Master Routes
    if (path === '/back-master') {
      const masterToken = localStorage.getItem('master_token');
      setView(masterToken ? 'MASTER' : 'MASTER_LOGIN');
      return;
    }

    if (path === '/backstage' || path.startsWith('/backstage/')) {
      const sellerToken = localStorage.getItem('seller_token');
      setView(sellerToken ? 'SELLER' : 'SELLER_LOGIN');
      return;
    }

    // 2. Landing Page
    if (path === '/' || path === '' || path === '/index.html') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('code')) {
        // Stay in LOADING, wait for Kakao processing useEffect to finish
        return;
      }
      setView('LANDING');
      return;
    }

    // 3. Store Routes
    let storeId = '';
    if (segments.length === 2 && segments[0] === 'store') {
      storeId = segments[1];
    } else if (segments.length === 1) {
      storeId = segments[0];
    }

    if (storeId) {
      setCurrentStoreId(storeId);
      const buyerToken = localStorage.getItem('buyer_token');
      if (buyerToken) {
        setView('FORM');
      } else {
        // Redirect to login but save where we were going
        localStorage.setItem('return_path', window.location.pathname);
        setView('LOGIN');
      }
    } else {
      setCurrentStoreId('');
      setView('NOT_FOUND');
    }
  };


  useEffect(() => {
    handleRoute();
    const onPopState = () => handleRoute();
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);
  const [orderSheets, setOrderSheets] = useState<OrderSheet[]>([]);


  // Load Profiles from LocalStorage
  const [buyerProfile, setBuyerProfile] = useState<UserProfile & { id?: string }>(() => {
    const saved = localStorage.getItem('buyer_profile');
    return saved ? JSON.parse(saved) : { name: '', phone: '' };
  });

  const [sellerProfile, setSellerProfile] = useState<UserProfile & { role?: string; id?: string }>(() => {
    const saved = localStorage.getItem('seller_profile');
    return saved ? JSON.parse(saved) : { name: '', phone: '' };
  });

  const [masterProfile, setMasterProfile] = useState<UserProfile & { role?: string; id?: string }>(() => {
    const saved = localStorage.getItem('master_profile');
    return saved ? JSON.parse(saved) : { name: '', phone: '' };
  });

  // Persist Profiles
  useEffect(() => { localStorage.setItem('buyer_profile', JSON.stringify(buyerProfile)); }, [buyerProfile]);
  useEffect(() => { localStorage.setItem('seller_profile', JSON.stringify(sellerProfile)); }, [sellerProfile]);
  useEffect(() => { localStorage.setItem('master_profile', JSON.stringify(masterProfile)); }, [masterProfile]);

  const fetchUserProfile = async () => {
    const token = localStorage.getItem('buyer_token');
    if (!token) return null;

    try {
      const user = await getProfile();
      const updatedProfile = {
        id: user.id,
        name: user.name ?? '',
        phone: user.phone ?? '',
      };
      setBuyerProfile(updatedProfile);
      return updatedProfile;
    } catch (err: any) {
      console.error('Failed to fetch profile:', err);
      if (err.response?.status === 401) {
        handleLogout();
      }
      return null;
    }
  };

  useEffect(() => {
    getActiveOrderSheet().then(sheet => {
      if (sheet) setOrderSheets([sheet]);
    }).catch(console.error);

    fetchUserProfile();
  }, []);


  useEffect(() => {
    if (view === 'LANDING') {
      getStores().then(setStores).catch(console.error);
    }
  }, [view]);

  // const handleUpdateProduct = async (product: Product) => {
  //   try {
  //     setProducts(prev => prev.map(p => p.id === product.id ? product : p));
  //     await updateProduct(product.id, product);
  //   } catch (e) {
  //     console.error(e);
  //     alert('상품 수정에 실패했습니다.');
  //     getProducts().then(setProducts).catch(console.error);
  //   }
  // };

  const handleLogout = () => {
    if (view === 'MASTER') {
      localStorage.removeItem('master_token');
      localStorage.removeItem('master_profile');
      setMasterProfile({ name: '', phone: '' });
      setView('MASTER_LOGIN');
    } else if (view === 'SELLER') {
      localStorage.removeItem('seller_token');
      localStorage.removeItem('seller_profile');
      setSellerProfile({ name: '', phone: '' });
      setView('SELLER_LOGIN');
    } else {
      localStorage.removeItem('buyer_token');
      localStorage.removeItem('buyer_profile');
      setBuyerProfile({ name: '', phone: '' });
      setView('LOGIN');
    }
  };

  const hasBuyerToken = () => !!localStorage.getItem('buyer_token');

  const [confirmedOrders, setConfirmedOrders] = useState<Order[]>([]); // For Seller/Admin view



  // Handle Kakao Login Redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    // Only process Kakao login if we are in a context that expects it (e.g. not specifically on seller/master login page)
    // Or if we decided Kakao is ONLY for buyers.
    if (code && view !== 'MASTER_LOGIN' && view !== 'SELLER_LOGIN') {
      window.history.replaceState({}, document.title, window.location.pathname);

      const processKakaoLogin = async () => {
        try {
          const tokenResponse = await getKakaoToken(code);
          const { access_token } = tokenResponse;

          if (!access_token) {
            console.error('No access_token in response');
            alert('카카오 토큰 발급 실패');
            return;
          }

          const response = await kakaoLogin(access_token);
          handleLoginSuccess(response.access_token, response.user);
        } catch (err) {
          console.error('Kakao Login Failed', err);
          alert('카카오 로그인 처리에 실패했습니다. 다시 시도해주세요.');
        }
      };
      processKakaoLogin();
    }
  }, []);
  const handleUpdateOrder = async (order: Order) => {
    try {
      const role = view === 'MASTER' ? 'ADMIN' : 'SELLER';
      const updatedOrder = await updateOrderStatus(order.id, order.status, role);
      setConfirmedOrders(prev => prev.map(o => o.id === updatedOrder.id ? updatedOrder : o));
      refreshData();
    } catch (e) {
      console.error('Failed to update order status:', e);
      alert('주문 상태 변경에 실패했습니다.');
    }
  };

  const handleLoginSuccess = (token: string, user: any) => {
    // Determine context based on view
    if (view === 'MASTER_LOGIN') {
      if (user.role !== 'ADMIN') {
        alert('관리자 권한이 없습니다.');
        return;
      }
      localStorage.setItem('master_token', token);
      setMasterProfile({ ...user, role: user.role, id: user.id });
      setView('MASTER');
    } else if (view === 'SELLER_LOGIN') {
      if (user.role !== 'SELLER' && user.role !== 'ADMIN') {
        alert('판매자 권한이 없습니다.');
        return;
      }
      localStorage.setItem('seller_token', token);
      setSellerProfile({ ...user, role: user.role, id: user.id });
      setView('SELLER');
    } else {
      // Buyer
      localStorage.setItem('buyer_token', token);
      setBuyerProfile({
        id: user.id,
        name: user.name ?? '',
        phone: user.phone ?? '',
      });
      fetchUserProfile(); // Sync latest from server

      const returnPath = localStorage.getItem('return_path');
      if (returnPath) {
        localStorage.removeItem('return_path');
        window.history.pushState({}, '', returnPath);
        handleRoute();
      } else {
        setView('FORM');
      }
    }
  };

  const refreshData = () => {
    if (view === 'SELLER' || view === 'MASTER') {
      getAdminProducts().then(setProducts).catch(console.error);
      getCustomerAliases().then(setCustomerAliases).catch(console.error);
    } else {
      getProducts().then(setProducts).catch(console.error);
    }

    const since90Days = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
    if (view === 'SELLER' && sellerProfile?.id) {
      getStores(sellerProfile.id).then(setStores).catch(console.error);
      getAllOrders(sellerProfile.id, since90Days).then(setConfirmedOrders).catch(console.error);
    } else {
      getStores().then(setStores).catch(console.error);
      getAllOrders(undefined, since90Days).then(setConfirmedOrders).catch(console.error);
    }
    getOrderSheets().then(setOrderSheets).catch(console.error);
  };

  useEffect(() => {
    if (view === 'MASTER') {
      refreshData();
    }
  }, [view]);

  const handleAddStore = async (name: string) => {
    try {
      await createStore(name);
      refreshData();
      alert('거점이 추가되었습니다.');
    } catch (e) {
      console.error(e);
      alert('거점 추가 실패');
    }
  };

  const handleUpdateStore = async (store: Store) => {
    try {
      const {
        id,
        name,
        orderType,
        isClosed,
        deliveryTime,
        closedAt,
        deliverySchedule,
        operationDayOfWeeks,
        operationStartHour,
        operationEndHour,
        pickupUnmannedStartHour,
        pickupUnmannedLabel,
        acceptsPickupTime,
        acceptsRequestMessage,
        acceptsOrderEdit,
        defaultDeliveryDay,
        deliveryDayOfWeek,
        orderCutoffDays,
      } = store;
      await updateStore(id, {
        name,
        orderType,
        isClosed,
        deliveryTime,
        closedAt,
        deliverySchedule,
        operationDayOfWeeks,
        operationStartHour,
        operationEndHour,
        pickupUnmannedStartHour,
        pickupUnmannedLabel,
        acceptsPickupTime,
        acceptsRequestMessage,
        acceptsOrderEdit,
        defaultDeliveryDay,
        deliveryDayOfWeek,
        orderCutoffDays,
      });
      refreshData();
      alert('거점이 수정되었습니다.');
    } catch (e) {
      console.error(e);
      alert('거점 수정 실패');
    }
  };

  const handleAddProduct = async (product: Product) => {
    try {
      await createProduct(product);
      refreshData();
      alert('상품이 추가되었습니다.');
    } catch (e) {
      console.error(e);
      alert('상품 추가 실패');
    }
  };

  const handleUpdateProduct = async (product: Product) => {
    try {
      const updatedProduct = await updateProductWithStockConfirmation(
        product.id,
        product,
      );
      if (!updatedProduct) {
        return;
      }
      refreshData();
      alert('상품이 수정되었습니다.');
    } catch (e) {
      console.error(e);
      alert('상품 수정 실패');
    }
  };

  const handleImpersonate = async (userId: string) => {
    try {
      const response = await impersonate(userId);
      // Save as buyer
      localStorage.setItem('buyer_token', response.access_token);
      localStorage.setItem('buyer_profile', JSON.stringify(response.user));
      setBuyerProfile(response.user);

      alert(`${response.user.username} 회원으로 접속합니다.`);
      setView('FORM');
      window.history.pushState({}, '', '/');
    } catch (e: any) {
      console.error('Impersonation failed:', e);
      alert('회원 접속에 실패했습니다.');
    }
  };

  const DevBadge = () => (
    import.meta.env.VITE_IS_DEV === 'true' ? (
      <div className="fixed top-2 right-2 z-[9999] pointer-events-none">
        <div className="bg-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-lg border border-white/20 animate-pulse">
          DEV
        </div>
      </div>
    ) : null
  );

  if (view === 'LOADING') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-4 border-emerald-100 border-t-emerald-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (view === 'NOT_FOUND') {
    return <NotFoundPage />;
  }

  if (view === 'LANDING') {
    return <LandingPage stores={stores} />;
  }

  if (view === 'LOGIN') {
    return <LoginPage onLoginSuccess={handleLoginSuccess} mode="KAKAO" />;
  }

  if (view === 'SELLER_LOGIN') {
    return <LoginPage onLoginSuccess={handleLoginSuccess} mode="SELLER" />;
  }

  if (view === 'MASTER_LOGIN') {
    return <LoginPage onLoginSuccess={handleLoginSuccess} mode="SELLER" title="마스터 로그인" />;
  }

  if (view === 'SELLER') {
    if (!sellerProfile.role || (sellerProfile.role !== 'ADMIN' && sellerProfile.role !== 'SELLER')) {
      setTimeout(() => setView('SELLER_LOGIN'), 0);
      return null;
    }

    return (
      <>
        <SellerDashboard
          user={sellerProfile}
          onExit={() => { setView('FORM'); window.scrollTo(0, 0); }}
          onLogout={handleLogout}
        />
        <DevBadge />
      </>
    );
  }

  if (view === 'MASTER') {
    if (!masterProfile.role || masterProfile.role !== 'ADMIN') {
      setTimeout(() => setView('MASTER_LOGIN'), 0);
      return null;
    }

    return (
      <>
        <MasterDashboard
          user={masterProfile}
          products={products}
          orders={confirmedOrders}
          orderSheets={orderSheets}
          stores={stores}
          onAddOrderSheet={c => setOrderSheets(prev => [c, ...prev])}
          onExit={() => { setView('FORM'); window.scrollTo(0, 0); }}
          onUpdateOrder={handleUpdateOrder}
          onAddStore={handleAddStore}
          onUpdateStore={handleUpdateStore}
          onAddProduct={handleAddProduct}
          onUpdateProduct={handleUpdateProduct}
          onRefreshProducts={refreshData}
          onLogout={handleLogout}
          onViewOrders={() => {
            window.history.pushState({}, '', '/backstage/orders');
            setView('SELLER');
          }}
          customerAliases={customerAliases}
          onUpdateAlias={async (phone, nick) => {
            try {
              await updateCustomerAlias(phone, nick);
              refreshData();
            } catch (e) {
              console.error(e);
              alert('닉네임 설정에 실패했습니다.');
            }
          }}
          onImpersonate={handleImpersonate}
        />
        <DevBadge />
      </>
    );
  }

  if (view === 'HISTORY') {
    return (
      <>
        <OrderHistory
          onBack={() => { setView('FORM'); window.scrollTo(0, 0); }}
          profile={buyerProfile}
          onUpdateProfile={setBuyerProfile}
          onLogout={handleLogout}
        />
        <DevBadge />
      </>
    );
  }

  if (view === 'CART') {
    return (
      <>
        <CartPage
          storeId={currentStoreId}
          buyerProfile={buyerProfile}
          setBuyerProfile={setBuyerProfile}
          onBack={() => { setView('FORM'); window.scrollTo(0, 0); }}
          onLogout={handleLogout}
        />
        <DevBadge />
      </>
    );
  }

  return (
    <>
      <OrderPage
        storeId={currentStoreId}
        buyerProfile={buyerProfile}
        setBuyerProfile={setBuyerProfile}
        onLogout={handleLogout}
        onViewHistory={() => {
          if (!hasBuyerToken()) {
            setView('LOGIN');
          } else {
            setView('HISTORY');
          }
          window.scrollTo(0, 0);
        }}
        onViewCart={() => {
          if (!hasBuyerToken()) {
            setView('LOGIN');
          } else {
            setView('CART');
          }
          window.scrollTo(0, 0);
        }}
      />
      <DevBadge />
    </>
  );
};

export default App;
